import { ProblemDetails } from '../ApiModel';
import { isProblemDetails } from './ErrorHelper';

export type ApiFetchOptions = {
    headers?: Record<string, string>;
    body?: BodyInit | null;
    cancellationToken?: AbortSignal;
};

type JsonFetchOptions = {
    headers?: Record<string, string>;
    body?: unknown;
    cancellationToken?: AbortSignal;
};

export function networkErrorAsProblemDetails(e: unknown): ProblemDetails {
    const maybeError = e as Error;
    return {
        type: 'NetworkError',
        title: 'Network Error',
        detail: maybeError.message,
        instance: maybeError.name,
    };
}

export function jsonErrorAsProblemDetails(e: unknown): ProblemDetails {
    const maybeError = e as Error;
    return {
        type: 'DataError',
        title: 'Response Data Error',
        detail: "The data from your request's response could not be parsed",
        instance: maybeError.name,
    };
}

export async function failedResponseAsProblemDetails(response: Response) {
    try {
        const respJson: ProblemDetails = (await response.json()) as ProblemDetails;
        //if we aren't problemdetails, fall through to catch
        if (!isProblemDetails(respJson)) {
            throw new Error(JSON.stringify(respJson));
        }

        return respJson;
    } catch {
        if (response.status >= 500) {
            return {
                type: 'ServerError',
                title: 'Server Error',
                status: response.status,
                detail: response.statusText,
            };
        }

        return {
            type: 'ClientError',
            title: "We couldn't process your request",
            status: response.status,
            detail: response.statusText,
        };
    }
}

export async function readJson<T>(response: Response): Promise<T | undefined> {
    try {
        if (response.ok) {
            const contentHeader = response.headers.get('content-type');
            if (!contentHeader || contentHeader.indexOf('application/json') === -1) {
                return undefined;
            }
            const res: T = (await response.json()) as T;
            return res;
        }
    } catch (e) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw jsonErrorAsProblemDetails(e);
    }

    const err = await failedResponseAsProblemDetails(response);
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw err;
}

// Shared fetch wrapper that handles common setup (method, cache, credentials, signal) and wraps
// network errors as ProblemDetails. Used by JsonFetch for JSON responses and directly by callers
// that need non-JSON responses (blob, text).
export async function ApiFetch(url: string, method: string, options: ApiFetchOptions = {}): Promise<Response> {
    try {
        return await fetch(url, {
            method: method.toUpperCase(),
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: options.headers,
            body: options.body,
            signal: options.cancellationToken,
        });
    } catch (e) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw networkErrorAsProblemDetails(e);
    }
}

// Like fetch except all requests and responses are considered to be JSON and are encoded/decoded automatically
// and all errors are returned as ProblemDetails instances
export async function JsonFetch<T>(
    url: string,
    method: string,
    options: JsonFetchOptions = {}
): Promise<T | undefined> {
    const normalizedMethod = method.toUpperCase();
    const headers: Record<string, string> = { Accept: 'application/json', ...(options.headers ?? {}) };

    if (
        (normalizedMethod === 'POST' || normalizedMethod === 'PUT' || normalizedMethod === 'PATCH') &&
        options.body !== undefined
    ) {
        headers['Content-Type'] = 'application/json';
    }

    const response = await ApiFetch(url, normalizedMethod, {
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        cancellationToken: options.cancellationToken,
    });

    if (response.status === 204) return undefined;

    return await readJson<T>(response);
}

export const DataError: ProblemDetails = {
    type: 'DataError',
    title: 'Data Error',
    detail: 'The network response was missing or corrupted',
    instance: 'DataError',
};

export async function ensureResult<T>(req: Promise<T | undefined>): Promise<T> {
    const res = await req;
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (res === undefined) throw DataError;
    return res;
}

export function notFoundAsUndefined<T>(Loader: () => Promise<T>): () => Promise<T | undefined> {
    return notFoundAsDefault(Loader, undefined);
}

export function notFoundAsDefault<T, TDefault>(
    Loader: () => Promise<T>,
    defaultValue: TDefault
): () => Promise<T | TDefault> {
    return async () => {
        try {
            const res = await Loader();
            return res;
        } catch (e) {
            if (isProblemDetails(e) && e.status === 404) {
                return defaultValue;
            }
            throw e;
        }
    };
}
